"use client";

import { useEffect } from "react";
import {
  useVybeAuthStore,
  presenceConnection,
  showAdminNotification,
} from "@/lib/vybe-auth";
import { usePlatform } from "@/platform/PlatformProvider";
import { useSyncedAccountsStore } from "@/lib/accounts/syncedAccounts";

/**
 * Opens the persistent Vybe Trader presence connection whenever a session token exists, and closes it on
 * logout. Sends the shell's `?client=` hint (web/desktop/ios), heartbeats, tracks the live connection
 * state (for the header chip), refreshes identity from the hub echo, and shows inbound admin notifications
 * as Sonner toasts. Renders nothing. This is the terminal's SECOND SignalR connection (trading is first).
 */
export function VybePresenceConnector() {
  const token = useVybeAuthStore((s) => s.token);
  const setPresenceConnected = useVybeAuthStore((s) => s.setPresenceConnected);
  const applyPresenceSelf = useVybeAuthStore((s) => s.applyPresenceSelf);
  const expireSession = useVybeAuthStore((s) => s.expireSession);
  const { platform } = usePlatform();

  useEffect(() => {
    if (!token) return;
    void presenceConnection.connect(token, platform, {
      onConnectedChange: setPresenceConnected,
      onSelf: applyPresenceSelf,
      onAdminNotification: showAdminNotification,
      // Realtime account sync: the CRM changed this user's trading accounts somewhere (portal,
      // another device, an admin). Signal only — re-fetch the list with our own bearer token.
      onAccountsChanged: () => void useSyncedAccountsStore.getState().refresh(),
      // Token rejected by the presence hub → expire the session so the UI prompts a fresh sign-in,
      // instead of showing "presence down, retrying" against a dead token until a manual re-login.
      onAuthRejected: () => expireSession(),
    });
    return () => {
      void presenceConnection.disconnect();
    };
  }, [token, platform, setPresenceConnected, applyPresenceSelf, expireSession]);

  return null;
}
